import React from 'react'
import { cn } from '@/lib/utils'

/**
 * GoLiveSwitch (W4) — the How-It-Works step-3 vignette: a Preview/Live switch
 * card with a status line. It is a REAL, interactive `<button role="switch">`
 * (allowed — a control, not navigation): the visitor can flip it and read the
 * two status lines. On reveal it also runs a single, gentle demo toggle
 * (Preview → Live → settles on Preview) so the second state is discoverable
 * without a click. After that one demo it rests — this widget contributes no
 * continuous motion to the section.
 *
 * Copy is VERBATIM from docs/marketing/2026-07-05-landing3-copy.md §W4 — customer
 * words "Preview" / "Live", PMS-neutral ("your PMS"). Do not edit strings here
 * without updating that doc + the claims register — the register is the gate every
 * marketing sentence passes ("no marketing sentence ships unless its claim is in this
 * register, with a status"), so a string edited only in this file is a published claim
 * nobody graded. Nothing enforces this; it is a habit, not a test.
 *
 * prefers-reduced-motion → no demo toggle, no transitions; starts in Preview and
 * only changes on an explicit click.
 */

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

export default function GoLiveSwitch({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [reduced] = React.useState(prefersReducedMotion)
  const [live, setLive] = React.useState(false)
  const [demoed, setDemoed] = React.useState(reduced)

  // A single one-shot demo when the card first scrolls into view: flip to Live,
  // hold, then settle back to Preview. Never loops.
  React.useEffect(() => {
    if (reduced || demoed) return
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setDemoed(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setDemoed(true)
          io.disconnect()
        }
      },
      { threshold: 0.5 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [reduced, demoed])

  React.useEffect(() => {
    if (reduced || !demoed) return
    const up = setTimeout(() => setLive(true), 700)
    const down = setTimeout(() => setLive(false), 2400)
    return () => {
      clearTimeout(up)
      clearTimeout(down)
    }
  }, [demoed, reduced])

  return (
    <div
      ref={ref}
      className={cn('mkt3-panel overflow-hidden', className)}
      role="img"
      aria-label="A switch between Preview, where rates are not being pushed, and Live, where rates are pushed to your PMS."
    >
      <div className="flex items-center justify-between gap-3 border-b border-[var(--mkt3-hairline)] px-5 py-3.5">
        <span className="mkt3-label">Go live</span>
        <span
          className={cn(
            'font-mono text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors duration-300',
            live ? 'text-teal-700' : 'text-slate-400'
          )}
        >
          {live ? 'Live' : 'Preview'}
        </span>
      </div>

      <div className="px-5 py-6">
        {/* The segmented switch */}
        <div className="flex items-center justify-center gap-4">
          <span
            className={cn(
              'text-sm font-medium transition-colors duration-300',
              live ? 'text-[var(--mkt3-muted)]' : 'text-[var(--mkt3-ink)]'
            )}
          >
            Preview
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={live}
            aria-label="Toggle between Preview and Live"
            onClick={() => setLive((v) => !v)}
            className={cn(
              'relative inline-flex h-7 w-[3.25rem] shrink-0 items-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2',
              reduced ? 'duration-0' : 'duration-300',
              live ? 'border-teal-600 bg-teal-600' : 'border-slate-300 bg-slate-200'
            )}
          >
            <span
              className={cn(
                'inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform',
                reduced ? 'duration-0' : 'duration-300',
                live ? 'translate-x-[1.75rem]' : 'translate-x-[0.2rem]'
              )}
            />
          </button>
          <span
            className={cn(
              'text-sm font-medium transition-colors duration-300',
              live ? 'text-teal-700' : 'text-[var(--mkt3-muted)]'
            )}
          >
            Live
          </span>
        </div>

        {/* Status line */}
        <div className="mt-5 flex items-center justify-center gap-2">
          <span
            aria-hidden="true"
            className={cn(
              'h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-300',
              live ? 'mkt3-live-dot bg-teal-500 shadow-[0_0_8px_rgba(13,148,136,0.7)]' : 'bg-slate-300'
            )}
          />
          <p className="text-center text-sm text-[var(--mkt3-body)]" aria-live="polite">
            {live ? 'Live — pushing rates to your PMS' : 'Preview — rates not being pushed'}
          </p>
        </div>
      </div>
    </div>
  )
}
